import type { JSX } from "preact";
import { Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { Auth } from "@gss/common/Auth";
import { resultsPaths as paths } from "@gss/common/Paths";
import { sweCompare } from "@gss/common/sweCompare";
import { BadgeLength } from "@gss/components/BadgeLength";
import { LinkButton } from "@gss/components/Button";
import { useMessages } from "@gss/components/Messages";
import { Table } from "@gss/components/Table";
import { DAO } from "./DAO";
import type { ResultMeasurement } from "./types";
import { Title } from "./util/Title";
import { useTranslation } from "./util/Translation";

type GroupName =
    | "measurementsNationalCurrent"
    | "measurementsNationalHistorical"
    | "measurementsRegional"
    | "measurementsPilot"
    | "measurementsUnknown";

interface MeasurementsGroup {
    name: GroupName;
    measurements: string[];
}

export function Measurements(): JSX.Element {
    const [measurementGroups, setMeasurementGroups] = useState<
        MeasurementsGroup[]
    >([]);
    const { translation } = useTranslation();
    const { addError } = useMessages();
    const t = translation.measurements;

    useEffect(() => {
        DAO.stats
            .getMeasurements()
            .then((measurements) => {
                measurements.sort((a, b) => sweCompare(a.name, b.name));
                setMeasurementGroups(groupMeasurements(measurements));
            })
            .catch((error: unknown) => {
                addError({
                    message: translation.error.loadMeasurements,
                    cause: error,
                });
            });
    }, []);

    const renderGroup = (group: MeasurementsGroup) => {
        if (group.measurements.length === 0) {
            return null;
        }
        return (
            <Fragment key={group.name}>
                <h3>
                    &nbsp;
                    {t[group.name]}
                    &nbsp;
                    <small>
                        <BadgeLength>{group.measurements}</BadgeLength>
                    </small>
                </h3>

                <Table striped hover>
                    {group.measurements.map((name) => (
                        <tr key={name}>
                            <td>
                                <Link
                                    href={paths.getMeasurementPath(name)}
                                    aria-label={name}
                                >
                                    {name}
                                </Link>
                            </td>
                        </tr>
                    ))}
                </Table>
            </Fragment>
        );
    };

    const renderAdminButtons = () => {
        if (!Auth.isAdmin()) {
            return null;
        }
        return (
            <div>
                <hr />
                <LinkButton href={paths.exports} variant="primary">
                    {t.exportResults}
                </LinkButton>
            </div>
        );
    };

    return (
        <>
            <Title>{t.results}</Title>
            <h1>{t.results}</h1>

            {measurementGroups.map(renderGroup)}

            {renderAdminButtons()}
        </>
    );
}

function groupMeasurements(
    measurements: ResultMeasurement[],
): MeasurementsGroup[] {
    const nationalCurrent: string[] = [];
    const nationalHistorical: string[] = [];
    const regional: string[] = [];
    const pilot: string[] = [];
    const unknown: string[] = [];

    for (const m of measurements) {
        if (
            m.processes.includes("Nationell frivillig") ||
            m.processes.includes("Nationell obligatorisk")
        ) {
            if (measurementIsHistorical(m.name)) {
                nationalHistorical.push(m.name);
            } else {
                nationalCurrent.push(m.name);
            }
        } else if (m.processes.includes("Regional")) {
            regional.push(m.name);
        } else if (m.processes.includes("Pilot")) {
            pilot.push(m.name);
        } else {
            unknown.push(m.name);
        }
    }

    return [
        {
            name: "measurementsNationalCurrent",
            measurements: nationalCurrent,
        },
        {
            name: "measurementsNationalHistorical",
            measurements: nationalHistorical,
        },
        {
            name: "measurementsRegional",
            measurements: regional,
        },
        {
            name: "measurementsPilot",
            measurements: pilot,
        },
        {
            name: "measurementsUnknown",
            measurements: unknown,
        },
    ];
}

// If the name ends with (YYYY-YYYY), it's a historical measurement, otherwise it's current.
function measurementIsHistorical(name: string): boolean {
    return /\(\d{4}-\d{4}\)$/u.test(name);
}
